import React, { useState, useEffect } from 'react';
import { Spinner } from 'react-bootstrap';
import { useEntidadeContext } from '../../EntidadeContext';
import { useAlertModal } from '../../AlertModalContext';
import axios from 'axios';
import InputMask from 'react-input-mask';

/* ── estilos base reutilizáveis ── */
const inputBase = {
  width: '100%', padding: '9px 12px', borderRadius: '8px',
  border: '1px solid #e5e7eb', fontSize: '13px', color: '#374151',
  fontFamily: 'inherit', outline: 'none', backgroundColor: '#fff',
  boxSizing: 'border-box', transition: 'border-color .15s, box-shadow .15s',
};
const inputReadOnly = { ...inputBase, backgroundColor: '#f9fafb', color: '#9ca3af', cursor: 'not-allowed' };
const fh = {
  onFocus: e => { e.target.style.borderColor = '#2563eb'; e.target.style.boxShadow = '0 0 0 3px rgba(37,99,235,.1)'; },
  onBlur:  e => { e.target.style.borderColor = '#e5e7eb'; e.target.style.boxShadow = 'none'; },
};

const Label = ({ children }) => (
  <label style={{ fontSize: '11px', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '.5px', display: 'block', marginBottom: '5px' }}>
    {children}
  </label>
);

const Field = ({ label, children }) => (
  <div style={{ marginBottom: '16px' }}>
    <Label>{label}</Label>
    {children}
  </div>
);

const SectionTitle = ({ icon, children }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: '24px 0 16px', paddingBottom: '10px', borderBottom: '2px solid #f1f5f9' }}>
    <div style={{ width: '28px', height: '28px', borderRadius: '7px', background: 'linear-gradient(135deg,#1e3a5f,#2563eb)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <svg width="14" height="14" fill="none" stroke="#fff" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={icon} />
      </svg>
    </div>
    <span style={{ fontSize: '13px', fontWeight: '700', color: '#1e3a5f' }}>{children}</span>
  </div>
);

const ICONS = {
  building: 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4',
  map:      'M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z M15 11a3 3 0 11-6 0 3 3 0 016 0z',
  save:     'M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4',
};

const EntidadeTab = () => {
  const { currentEntidade, setCurrentEntidade } = useEntidadeContext();
  const { showAlertModal } = useAlertModal();

  const [formData, setFormData] = useState({
    nome: '', cnpj: '', email: '', telefone: '', whatsapp_funil: '',
    cep: '', endereco: '', numero: '', bairro: '', cidade: '', estado: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError]         = useState(null);
  const [isChanged, setIsChanged] = useState(false);

  useEffect(() => {
    if (currentEntidade) {
      setFormData({
        nome: currentEntidade.nome || '', cnpj: currentEntidade.cnpj || '',
        email: currentEntidade.email || '', telefone: currentEntidade.telefone || '',
        whatsapp_funil: currentEntidade.whatsapp_funil || '',
        cep: currentEntidade.cep || '', endereco: currentEntidade.endereco || '',
        numero: currentEntidade.numero || '', bairro: currentEntidade.bairro || '',
        cidade: currentEntidade.cidade || '', estado: currentEntidade.estado || '',
      });
      setIsChanged(false);
    }
  }, [currentEntidade]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setIsChanged(true);
  };

  const handleCepChange = async (e) => {
    const cep = e.target.value.replace(/\D/g, '');
    handleChange(e);
    if (cep.length === 8) {
      try {
        const response = await axios.get(`https://viacep.com.br/ws/${cep}/json/`);
        const data = response.data;
        if (!data.erro) {
          setFormData(prev => ({ ...prev, endereco: data.logradouro, bairro: data.bairro, cidade: data.localidade, estado: data.uf }));
        }
      } catch {}
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!currentEntidade) { setError('Nenhuma entidade selecionada.'); return; }
    setIsLoading(true); setError(null);
    try {
      const response = await axios.put(`/api/entidades/${currentEntidade.id}`, formData);
      setCurrentEntidade(response.data.entidade);
      showAlertModal('Sucesso', 'Dados da entidade atualizados com sucesso!', 'success');
      setIsChanged(false);
    } catch (err) {
      const apiError = err.response?.data?.message || 'Ocorreu um erro ao atualizar os dados.';
      setError(apiError);
      showAlertModal('Erro', apiError, 'danger');
    } finally { setIsLoading(false); }
  };

  const ro = (field) => !!currentEntidade?.[field];

  return (
    <div style={{ fontFamily: "'Inter','Segoe UI',sans-serif" }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');`}</style>

      {/* ── HEADER ── */}
      <div style={{ background: 'linear-gradient(135deg,#1e3a5f,#2563eb)', borderRadius: '14px', padding: '20px 24px', marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '14px' }}>
        <div style={{ width: '44px', height: '44px', borderRadius: '12px', backgroundColor: 'rgba(255,255,255,.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <svg width="22" height="22" fill="none" stroke="#fff" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d={ICONS.building} />
          </svg>
        </div>
        <div>
          <div style={{ fontSize: '16px', fontWeight: '800', color: '#fff' }}>Dados da Entidade</div>
          <div style={{ fontSize: '12px', color: 'rgba(255,255,255,.7)' }}>Informações gerais e endereço da clínica</div>
        </div>
      </div>

      {/* ── FORMULÁRIO ── */}
      <form onSubmit={handleSubmit}>
        <div style={{ backgroundColor: '#fff', borderRadius: '14px', border: '1px solid #e5e7eb', padding: '24px', marginBottom: '16px' }}>

          <SectionTitle icon={ICONS.building}>Informações Gerais</SectionTitle>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 20px' }}>
            <Field label="Nome da Clínica">
              <input name="nome" type="text" value={formData.nome} onChange={handleChange}
                style={ro('nome') ? inputReadOnly : inputBase} readOnly={ro('nome')}
                {...(!ro('nome') ? fh : {})} />
            </Field>

            <Field label="CNPJ">
              <InputMask
                mask="99.999.999/9999-99"
                value={formData.cnpj}
                onChange={handleChange}
                readOnly={ro('cnpj')}
                {...(!ro('cnpj') ? fh : {})}
                >
                {inputProps => <input {...inputProps} name="cnpj" style={ro('cnpj') ? inputReadOnly : inputBase} />}
                </InputMask>
            </Field>

            <Field label="E-mail Principal">
              <input name="email" type="email" value={formData.email} onChange={handleChange}
                style={ro('email') ? inputReadOnly : inputBase} readOnly={ro('email')}
                {...(!ro('email') ? fh : {})} />
            </Field>

            <Field label="Telefone Principal">
              {/* Telefone */}
                <InputMask
                mask="(99) 9999-9999"
                value={formData.telefone}
                onChange={handleChange}
                readOnly={ro('telefone')}
                {...(!ro('telefone') ? fh : {})}
                >
                {inputProps => <input {...inputProps} name="telefone" style={ro('telefone') ? inputReadOnly : inputBase} />}
                </InputMask>
            </Field>
          </div>

          <div style={{ maxWidth: '50%', paddingRight: '10px' }}>
            <Field label="WhatsApp (Funil de Vendas)">
              {/* WhatsApp Funil */}
                <InputMask
                mask="(99) 99999-9999"
                value={formData.whatsapp_funil}
                onChange={handleChange}
                {...fh}
                >
                {inputProps => <input {...inputProps} name="whatsapp_funil" style={inputBase} />}
                </InputMask>
              <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '5px' }}>
                Este número será usado para redirecionar leads das páginas de captura.
              </div>
            </Field>
          </div>
        </div>

        {/* ── ENDEREÇO ── */}
        <div style={{ backgroundColor: '#fff', borderRadius: '14px', border: '1px solid #e5e7eb', padding: '24px', marginBottom: '16px' }}>
          <SectionTitle icon={ICONS.map}>Endereço Principal</SectionTitle>

          <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: '0 20px' }}>
            <Field label="CEP">
              {/* CEP */}
                <InputMask
                mask="99999-999"
                value={formData.cep}
                onChange={handleCepChange}
                readOnly={ro('cep')}
                {...(!ro('cep') ? fh : {})}
                >
                {inputProps => <input {...inputProps} name="cep" style={ro('cep') ? inputReadOnly : inputBase} />}
                </InputMask>
            </Field>
            <Field label="Endereço">
              <input name="endereco" type="text" value={formData.endereco} onChange={handleChange}
                style={ro('endereco') ? inputReadOnly : inputBase} readOnly={ro('endereco')}
                {...(!ro('endereco') ? fh : {})} />
            </Field>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '0 20px' }}>
            <Field label="Número">
              <input name="numero" type="text" value={formData.numero} onChange={handleChange}
                style={ro('numero') ? inputReadOnly : inputBase} readOnly={ro('numero')}
                {...(!ro('numero') ? fh : {})} />
            </Field>
            <Field label="Bairro">
              <input name="bairro" type="text" value={formData.bairro} onChange={handleChange}
                style={ro('bairro') ? inputReadOnly : inputBase} readOnly={ro('bairro')}
                {...(!ro('bairro') ? fh : {})} />
            </Field>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 100px', gap: '0 20px' }}>
            <Field label="Cidade">
              <input name="cidade" type="text" value={formData.cidade} onChange={handleChange}
                style={ro('cidade') ? inputReadOnly : inputBase} readOnly={ro('cidade')}
                {...(!ro('cidade') ? fh : {})} />
            </Field>
            <Field label="Estado (UF)">
              <input name="estado" type="text" value={formData.estado} onChange={handleChange}
                maxLength="2" style={ro('estado') ? inputReadOnly : inputBase} readOnly={ro('estado')}
                {...(!ro('estado') ? fh : {})} />
            </Field>
          </div>
        </div>

        {/* ── ERRO ── */}
        {error && (
          <div style={{ padding: '12px 16px', borderRadius: '10px', backgroundColor: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', fontSize: '13px', marginBottom: '16px' }}>
            {error}
          </div>
        )}

        {/* ── BOTÃO SALVAR ── */}
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button
            type="submit"
            disabled={isLoading || !isChanged}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '8px',
              padding: '10px 24px', borderRadius: '10px', border: 'none',
              background: (isLoading || !isChanged) ? '#93c5fd' : 'linear-gradient(135deg,#1e3a5f,#2563eb)',
              color: '#fff', fontSize: '13px', fontWeight: '700',
              cursor: (isLoading || !isChanged) ? 'not-allowed' : 'pointer',
              fontFamily: 'inherit', transition: 'opacity .15s',
            }}
          >
            {isLoading
              ? <><Spinner as="span" animation="border" size="sm" style={{ width: '14px', height: '14px', borderWidth: '2px' }} /> Salvando...</>
              : <><svg width="14" height="14" fill="none" stroke="#fff" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={ICONS.save} /></svg> Salvar Alterações</>
            }
          </button>
        </div>
      </form>
    </div>
  );
};

export default EntidadeTab;
